import React, { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, ArrowLeft } from 'lucide-react';
import { useEstimator } from '../../context/EstimatorContext';
import { track } from '../../lib/site';
import { useEstimatorForm } from '../../hooks/useEstimatorForm';
import {
  STEP_TITLES, TypeStep, AreaStep, FloorsStep, LocationStep, StageStep, ContactStep, SuccessScreen,
} from './steps';

const stepMotion = {
  initial: { opacity: 0, x: 24 },
  animate: { opacity: 1, x: 0, transition: { duration: 0.32, ease: [0.2, 0.8, 0.2, 1] } },
  exit: { opacity: 0, x: -24, transition: { duration: 0.2, ease: [0.4, 0, 1, 1] } },
};

const ADVANCE_DELAY_MS = 220;

export const EstimatorOverlay = () => {
  const { isOpen, preselect, closeEstimator } = useEstimator();
  const [step, setStep] = React.useState(0);
  const {
    form, setForm, set, submitting, submitted, error,
    canContinueLocation, canSubmit, submit, reset,
  } = useEstimatorForm();

  useEffect(() => {
    if (isOpen) {
      track('estimator_started', { preselect: preselect || 'none' });
      reset(preselect);
      setStep(preselect ? 1 : 0);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
    // reset/preselect are intentionally read only when the overlay opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && step === 5) track('lead_form_viewed', {});
  }, [isOpen, step]);

  const advance = (next) => {
    track('estimator_step_completed', { step: step + 1 });
    setStep(next);
  };

  const selectAndAdvance = (key, next, extraTrack) => (value) => {
    set(key)(value);
    if (extraTrack) extraTrack(value);
    setTimeout(() => advance(next), ADVANCE_DELAY_MS);
  };

  if (!isOpen) return null;

  const progress = submitted ? 100 : ((step + 1) / 6) * 100;

  const stepScreens = [
    <TypeStep
      key="type"
      form={form}
      onSelect={selectAndAdvance('project_type', 1, (v) => track('project_type_selected', { placement: 'estimator', type: v }))}
    />,
    <AreaStep
      key="area"
      form={form}
      set={set}
      onSelect={(a) => {
        setForm((f) => ({ ...f, built_up_area: a, custom_area: '' }));
        setTimeout(() => advance(2), ADVANCE_DELAY_MS);
      }}
      onManualContinue={() => advance(2)}
    />,
    <FloorsStep key="floors" form={form} onSelect={selectAndAdvance('floors', 3)} />,
    <LocationStep key="location" form={form} set={set} canContinue={canContinueLocation} onContinue={() => advance(4)} />,
    <StageStep key="stage" form={form} onSelect={selectAndAdvance('project_stage', 5)} />,
    <ContactStep key="contact" form={form} set={set} error={error} submitting={submitting} canSubmit={canSubmit} onSubmit={submit} />,
  ];

  return (
    <div className="fixed inset-0 z-50 bg-background overflow-y-auto" role="dialog" aria-modal="true" aria-label="Project estimator" data-testid="estimator-overlay">
      {/* Top bar */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <img src="/assets/melopa-logo-transparent.png" alt="MELOPA INFRA" className="h-11 w-auto" />
          <div className="flex items-center gap-4">
            {!submitted && (
              <span className="font-mono-tech text-xs text-muted-foreground" data-testid="estimator-step-indicator">
                {String(step + 1).padStart(2, '0')} / 06
              </span>
            )}
            <button
              type="button"
              aria-label="Close estimator"
              data-testid="estimator-close-button"
              onClick={closeEstimator}
              className="p-2 rounded-md hover:bg-muted transition-[background-color]"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        <div className="h-0.5 bg-muted">
          <div className="h-full bg-[#155E6D] transition-[width] duration-500" style={{ width: `${progress}%` }} />
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14 pb-28">
        <AnimatePresence mode="wait">
          {submitted ? (
            <motion.div key="success" {...stepMotion}>
              <SuccessScreen submitted={submitted} onClose={closeEstimator} />
            </motion.div>
          ) : (
            <motion.div key={`step-${step}`} {...stepMotion}>
              <p className="font-mono-tech text-xs tracking-[0.28em] uppercase text-[#B87333]">Project Estimate</p>
              <h2 className="font-display font-semibold text-2xl sm:text-3xl lg:text-4xl tracking-tight mt-3" data-testid="estimator-step-title">
                {STEP_TITLES[step]}
              </h2>
              {step === 0 && (
                <p className="mt-3 text-sm text-muted-foreground">Answer a few questions and our team will review your project requirement.</p>
              )}

              {stepScreens[step]}

              {/* Back nav */}
              {step > 0 && (
                <button
                  type="button"
                  data-testid="estimator-back-button"
                  onClick={() => setStep(step - 1)}
                  className="mt-10 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-[color]"
                >
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
